import { useEffect, useRef } from 'react';

const isDevelopment = process.env.NODE_ENV !== 'production';
// Prefer explicit API base if provided at build time; falls back to sensible defaults
const API_URL = (
  process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.trim()
) || (
  isDevelopment
    ? (window.location.hostname === 'localhost'
        ? 'http://localhost:5000'
        : `${window.location.protocol}//${window.location.hostname}:5000`)
    : `${window.location.origin}/api`
);

export const useDataUpdates = (onUpdate, handlers = {}) => {
  const eventSourceRef = useRef(null);
  const lastUpdateTime = useRef(Date.now());
  const updateThrottleMs = 1000; // Minimum time between updates
  const reconnectTimeoutRef = useRef(null);
  const onPivotDelta = handlers.onPivotDelta;
  const onFdcDelta = handlers.onFdcDelta;

  useEffect(() => {
    const setupEventSource = () => {
      console.log('Setting up SSE connection to:', `${API_URL}/data-updates`);
      
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      // Cache-buster to defeat any intermediate caches
      const url = `${API_URL}/data-updates?ts=${Date.now()}`;
      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        console.log('SSE connection opened');
        // Clear any pending reconnection attempts
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      };

      eventSource.addEventListener('update', (event) => {
        const now = Date.now();
        if (now - lastUpdateTime.current < updateThrottleMs) {
          return; // Skip update if too soon
        }

        try {
          const data = JSON.parse(event.data);
          if (data.type === 'update' && data.status === 'success') {
            console.log('Processing update:', data);
            lastUpdateTime.current = now;
            onUpdate();
          }
        } catch (error) {
          console.error('Error processing update event:', error);
        }
      });

      eventSource.addEventListener('pivot_delta', (event) => {
        try {
          if (typeof onPivotDelta === 'function') {
            const data = JSON.parse(event.data);
            onPivotDelta(data);
          }
        } catch (error) {
          console.error('Error processing pivot_delta event:', error);
        }
      });

      eventSource.addEventListener('fdc_delta', (event) => {
        try {
          if (typeof onFdcDelta === 'function') {
            const data = JSON.parse(event.data);
            onFdcDelta(data);
          }
        } catch (error) {
          console.error('Error processing fdc_delta event:', error);
        }
      });

      eventSource.onerror = (error) => {
        console.error('SSE connection error:', error);
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
          
          // Implement exponential backoff for reconnection
          if (!reconnectTimeoutRef.current) {
            reconnectTimeoutRef.current = setTimeout(() => {
              console.log('Attempting to reconnect SSE...');
              setupEventSource();
              reconnectTimeoutRef.current = null;
            }, 5000);
          }
        }
      };
    };

    setupEventSource();

    return () => {
      console.log('Cleaning up SSE connection');
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [onUpdate]);
};